import { useEffect, useState } from "react";
import { ApiSchemaAppointmentIdByPatient } from "../models/api";
import { ApiAppointment } from "../core/apis";
import { UtilityDate } from "../utils";

interface IProps {
  patientSKey?: string | undefined;
  startDate: Date | undefined;
  endDate: Date | undefined;
  appointmentId?: string | undefined;
}

const initialOverlappedStatus = {
  exist: false,
  sKey: "",
  from: "",
  to: "",
};

export function useOverlappedAppointment({
  patientSKey,
  startDate,
  endDate,
  appointmentId
}: IProps) {
  const [existingAppointment, setExistingAppointment] =
    useState<ApiSchemaAppointmentIdByPatient[]>();
  const [overlappedAppointment, setOverlappedAppointment] = useState(
    initialOverlappedStatus
  );
  const [isFetchingAppointment, setIsFetchingAppointment] = useState(false);
  const haveIssue = (() =>
    !existingAppointment ||
    isFetchingAppointment ||
    overlappedAppointment.exist)();

  useEffect(() => {
    if (!patientSKey) {
      return;
    }

    setOverlappedAppointment(initialOverlappedStatus);

    const fetchPatientAppointmentInfo = async () => {
      setIsFetchingAppointment(true);
      const response = await ApiAppointment.GetAppointmentIdByPatient(
        patientSKey,
        appointmentId
      );
      if (response.isSuccess) {
        setExistingAppointment(response.data);
      }
      setIsFetchingAppointment(false);
    };

    fetchPatientAppointmentInfo();
  }, [appointmentId, patientSKey]);

  useEffect(() => {
    if (!existingAppointment || !startDate || !endDate) {
      return;
    }

    const checkOverlap = () => {
      for (const appointment of existingAppointment) {
        const existingStartDate = UtilityDate.getDateOnly(
          appointment.appointmentStartDate
        );
        const existingEndDate = UtilityDate.getDateOnly(
          appointment.appointmentEndDate
        );
        const currentStartDate = UtilityDate.getDateOnly(startDate.toString());
        const currentEndDate = UtilityDate.getDateOnly(endDate.toString());
        if (
          (currentStartDate >= existingStartDate &&
            currentStartDate <= existingEndDate) ||
          (currentEndDate >= existingStartDate &&
            currentEndDate <= existingEndDate)
        ) {
          setOverlappedAppointment({
            exist: true,
            sKey: appointment.sKey,
            from: existingStartDate.toLocaleDateString(),
            to: existingEndDate.toLocaleDateString(),
          });
          break;
        } else {
          setOverlappedAppointment((prev) => ({ ...prev, exist: false }));
        }
      }
    };

    checkOverlap();
  }, [existingAppointment, startDate, endDate]);

  return {
    isFetchingAppointment,
    overlappedAppointment,
    existingAppointment,
    haveIssue,
    setExistingAppointment,
    setIsFetchingAppointment,
    setOverlappedAppointment,
  };
}
